<script setup lang="tsx">
  import { ChevronDownIcon, PencilIcon, PlusIcon, XIcon } from '@heroicons/vue/solid';
  import type {
    ResponseStatus,
    InfoBlock,
    AdvancePaymentResponse,
    PaymentUpdateResponse,
    AdvanceSearchOptions,
    AdvancePaymentOptions,
    GetExpenseResponse,
    OrgBankPaymentOption
  } from '~/types';
  import DrawerLayout from '~/components/UI/Drawers/DrawerLayout.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomDate from '~/components/UI/Forms/CustomDate.vue';
  import CustomRupeeInput from '~/components/UI/Forms/CustomRupeeInput.vue';
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomDropdownWithSearch from '~/components/UI/Forms/CustomDropdownWithSearch.vue';
  import { get, post } from '~/services/donationScreen';
  import { formatDateWithSlash, toDateTime } from '~/functions/dateTimeUtilities';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import type { Dayjs } from 'dayjs';
  import {
    bookOfAccounts,
    paymentModes,
    updatePaymentFormData,
    parseFormDataToState
  } from '~/components/Expenses/Table/expenseTableUtils';
  import ExpenseTotalAmountModal from '~/components/Expenses/Table/ExpenseTotalAmountModal.vue';
  import AuditLogs from '~/components/shared/RoutedDrawers/RoutedDrawerComponents/AuditLogs.vue';
  import { cloneDeep } from 'lodash';
  import PaymentSummaryFundRow from '~/components/Expenses/Table/PaymentSummaryFundRow.vue';
  import PaymentSummaryBankRow from '~/components/Expenses/Table/PaymentSummaryBankRow.vue';
  import PaymentLoadingState from '~/components/UI/Drawers/PaymentLoadingState.vue';
  import CustomDropDownAdvanceUIDSearch from '~/components/UI/Forms/CustomDropDownAdvanceUIDSearch.vue';

  const { onClose, identifier } = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();
  const { role } = useAuth();
  const responseState = ref<ResponseStatus>('success');
  const getDropdownDataStatus = ref<ResponseStatus>('idle');
  const formState = ref<InfoBlock[]>([]);
  const paymentErrorMessage = ref<string>('');

  // Debounced per-row amount re-validation so the error isn't recomputed on every keystroke.
  const amountCheckTimers = new Map<number, ReturnType<typeof setTimeout>>();

  // Per-row error stays until the row is re-validated or the user closes the alert.
  const setPaymentError = (paymentId: number, error: string) => {
    formState.value = formState.value.map(p => (p.id === paymentId ? { ...p, error } : p));
  };
  const expenseDetails = reactive<{
    totalAmountPaid: null | number;
    balancePayments: number;
    balanceForFCRA: number | string;
    balanceForNonFCRA: number | string;
    remainingToBeAllocated: number;
    totalInvoiceAmount: number;
    totalExpenseAllocationNonFcra: number;
    totalExpenseAllocationFcra: number;
    totalExpenseUnallocated: number;
    totalUnallocatedTotalAmount: number;
    totalExpenseAllocation: number;
    tdsNonFcra: number;
    tdsFcra: number;
    unallocatedTdsAmount: number;
    totalTdsAmount: number;
    payableNetOfTdsNonFcra: number;
    payableNetOfTdsFcra: number;
    payableNetOfTdsUnallocated: number;
    payableNetOfTds: number;
    auditLog: any[];
    grantsAndBudgetAllocation: any[];
    remaining_to_be_unallocated: number;
  }>({
    totalAmountPaid: null,
    balancePayments: 0,
    balanceForFCRA: 0,
    balanceForNonFCRA: 0,
    remainingToBeAllocated: 0,
    totalInvoiceAmount: 0,
    totalExpenseAllocationNonFcra: 0,
    totalExpenseAllocationFcra: 0,
    totalExpenseUnallocated: 0,
    totalUnallocatedTotalAmount: 0,
    totalExpenseAllocation: 0,
    tdsNonFcra: 0,
    tdsFcra: 0,
    unallocatedTdsAmount: 0,
    totalTdsAmount: 0,
    payableNetOfTdsNonFcra: 0,
    payableNetOfTdsFcra: 0,
    payableNetOfTdsUnallocated: 0,
    payableNetOfTds: 0,
    auditLog: [],
    grantsAndBudgetAllocation: [],
    remaining_to_be_unallocated: 0
  });
  // Payment Summary is open by default when the drawer opens; the user can collapse it
  // manually (v-model takes precedence over default-active-key once bound, so this must
  // start as ['1'] rather than empty).
  const activeKey = ref<string[]>(['1']);
  const advanceSearchOptions = ref<AdvanceSearchOptions[]>([]);
  // Bank Details options — bucketed by FCRA / Non-FCRA, filtered per row to match that
  // payment's selected Book of Accounts. Optional field: not required on submit. Always
  // disabled for advance-linked payments (auto-filled from the linked advance instead).
  const orgBanksList = ref<{ FCRA: OrgBankPaymentOption[]; 'Non-FCRA': OrgBankPaymentOption[] }>({
    FCRA: [],
    'Non-FCRA': []
  });
  const bankOptionsFor = (bookOfAccountsValue: string) => {
    const options = bookOfAccountsValue === 'Non-FCRA' ? orgBanksList.value['Non-FCRA'] : orgBanksList.value.FCRA;
    return options.map(bank => ({
      title: bank.label,
      value: bank.uid.toString(),
      hiddenFromList: bank.is_disabled
    }));
  };
  const updatePaymentStatus = ref<ResponseStatus>('idle');
  const expenseInfo = ref<any>({});
  const approvalStatus = ref<string>('');
  const is_current_approver = ref<boolean>(false);
  const isFinalApprover = ref<boolean>(false);
  const cancelled = ref<boolean>(false);

  const paymentsNonFcra = computed(() => {
    return formState.value.reduce((acc, payment) => {
      if (!payment.isAdvance && payment.bookOfAccounts === 'Non-FCRA') {
        return acc + (Number(payment.amount) || 0);
      }
      return acc;
    }, 0);
  });

  const paymentsFcra = computed(() => {
    return formState.value.reduce((acc, payment) => {
      if (!payment.isAdvance && payment.bookOfAccounts === 'FCRA') {
        return acc + (Number(payment.amount) || 0);
      }
      return acc;
    }, 0);
  });

  // A payment with no Book of Accounts selected falls into the same "Unallocated"
  // bucket as a line item with no Book of Accounts (see getPaymentSummary).
  const paymentsUnallocated = computed(() => {
    return formState.value.reduce((acc, payment) => {
      if (!payment.isAdvance && payment.bookOfAccounts !== 'FCRA' && payment.bookOfAccounts !== 'Non-FCRA') {
        return acc + (Number(payment.amount) || 0);
      }
      return acc;
    }, 0);
  });

  const totalPayments = computed(() => {
    return +paymentsNonFcra.value + +paymentsFcra.value + +paymentsUnallocated.value;
  });

  const paymentsViaAdvancesNonFcra = computed(() => {
    return formState.value.reduce((acc, payment) => {
      if (payment.isAdvance && payment.bookOfAccounts === 'Non-FCRA') {
        return acc + (Number(payment.amount) || 0);
      }
      return acc;
    }, 0);
  });

  const paymentsViaAdvancesFcra = computed(() => {
    return formState.value.reduce((acc, payment) => {
      if (payment.isAdvance && payment.bookOfAccounts === 'FCRA') {
        return acc + (Number(payment.amount) || 0);
      }
      return acc;
    }, 0);
  });

  const paymentsViaAdvancesUnallocated = computed(() => {
    return formState.value.reduce((acc, payment) => {
      if (payment.isAdvance && payment.bookOfAccounts !== 'FCRA' && payment.bookOfAccounts !== 'Non-FCRA') {
        return acc + (Number(payment.amount) || 0);
      }
      return acc;
    }, 0);
  });

  const totalPaymentsViaAdvances = computed(() => {
    return +paymentsViaAdvancesNonFcra.value + +paymentsViaAdvancesFcra.value + +paymentsViaAdvancesUnallocated.value;
  });

  const totalPaymentsAndAdvancesNonFcra = computed(() => {
    return paymentsNonFcra.value + paymentsViaAdvancesNonFcra.value;
  });

  const totalPaymentsAndAdvancesFcra = computed(() => {
    return paymentsFcra.value + paymentsViaAdvancesFcra.value;
  });

  const totalPaymentsAndAdvancesUnallocated = computed(() => {
    return paymentsUnallocated.value + paymentsViaAdvancesUnallocated.value;
  });

  const totalPaymentAndAdvancesBoth = computed(() => {
    return (
      +totalPaymentsAndAdvancesNonFcra.value +
      +totalPaymentsAndAdvancesFcra.value +
      +totalPaymentsAndAdvancesUnallocated.value
    );
  });

  const balanceNonFcra = computed(() => {
    return expenseDetails.payableNetOfTdsNonFcra - totalPaymentsAndAdvancesNonFcra.value;
  });

  const balanceFcra = computed(() => {
    return expenseDetails.payableNetOfTdsFcra - totalPaymentsAndAdvancesFcra.value;
  });

  const balanceUnallocated = computed(() => {
    return expenseDetails.payableNetOfTdsUnallocated - totalPaymentsAndAdvancesUnallocated.value;
  });

  const balanceBoth = computed(() => {
    return +expenseDetails.payableNetOfTds - +totalPaymentAndAdvancesBoth.value;
  });

  // Payment Summary — bank-wise breakdown, grouped by book of account then bank account.
  // Two line items sharing the same book of account + bank account are summed into one
  // row; anything else (different bank, or a different book of account) gets its own row.
  // Line items with no bank selected fall into a single "Bank not selected" row.
  const nonFcraExpanded = ref<boolean>(true);
  const fcraExpanded = ref<boolean>(true);

  // Line items store their bank as the "{alias} - {account_no}" label; payments store it
  // as the org bank's uid (bankUid). This maps uid -> label so a payment's bank can be
  // matched against the Payment Summary's bank rows (which are keyed by label).
  const bankLabelByUid = computed(() => {
    const map = new Map<string, string>();
    [...orgBanksList.value.FCRA, ...orgBanksList.value['Non-FCRA']].forEach(bank => {
      map.set(bank.uid.toString(), bank.label);
    });
    return map;
  });

  // Real payments made so far against each bank, split the same way the fund-level rows
  // already are — regular payments vs advance-linked ones — so a bank's sub-row can show
  // actual Payments / Via Advances / Paid (B) / Balance (A-B) instead of "-". Nested by
  // book of account first, since "Bank not selected" is a shared fallback label that can
  // legitimately occur under both FCRA and Non-FCRA independently. A payment recorded
  // without picking a bank folds into that same "Bank not selected" bucket.
  const bankPaymentTotals = computed(() => {
    const map = new Map<string, Map<string, { payments: number; viaAdvances: number }>>();
    formState.value.forEach(payment => {
      const bookOfAccount = payment.bookOfAccounts;
      if (bookOfAccount !== 'FCRA' && bookOfAccount !== 'Non-FCRA') return;
      const label = (payment.bankUid && bankLabelByUid.value.get(payment.bankUid)) || 'Bank not selected';
      if (!map.has(bookOfAccount)) map.set(bookOfAccount, new Map());
      const bookMap = map.get(bookOfAccount)!;
      const existing = bookMap.get(label) || { payments: 0, viaAdvances: 0 };
      if (payment.isAdvance) {
        existing.viaAdvances += payment.amount || 0;
      } else {
        existing.payments += payment.amount || 0;
      }
      bookMap.set(label, existing);
    });
    return map;
  });

  // Real bank labels used by this expense's own line items, per book of account — used to
  // block a payment (or advance) from being recorded against a bank that isn't part of the
  // expense's own allocation. "Bank not selected" line items don't count as a real bank here.
  const lineItemBankLabelsByBook = computed(() => {
    const map = new Map<string, Set<string>>();
    (expenseDetails.grantsAndBudgetAllocation || []).forEach((item: any) => {
      const book = item.accountInfo?.bookOfAccount;
      if (book !== 'FCRA' && book !== 'Non-FCRA') return;
      const bank = item.accountInfo?.bankAccount;
      if (!bank) return;
      if (!map.has(book)) map.set(book, new Set());
      map.get(book)!.add(bank);
    });
    return map;
  });

  // A payment/advance with a bank selected must be against a bank the expense's own line
  // items actually allocate to for that book of account — otherwise it'd introduce a brand
  // new Payment Summary row backed by no allocation.
  const bankNotInLineItemsError = (payment: InfoBlock): string => {
    const bookOfAccount = payment.bookOfAccounts;
    if (bookOfAccount !== 'FCRA' && bookOfAccount !== 'Non-FCRA') return '';
    if (!payment.bankUid) return '';
    const label = bankLabelByUid.value.get(payment.bankUid);
    if (!label) return '';
    const allowedBanks = lineItemBankLabelsByBook.value.get(bookOfAccount);
    if (!allowedBanks || !allowedBanks.has(label)) {
      return `Bank "${label}" is not available in the selected line items of this expense (${bookOfAccount}).`;
    }
    return '';
  };

  // Advance-linked payment can't draw more than the advance line item's remaining outstanding (checked client-side, shown inline).
  const advanceOutstandingError = (payment: InfoBlock): string => {
    if (!payment.isAdvance || !payment.advancePaymentId) return '';
    if (payment.amount === null || payment.amount === undefined) return '';
    const outstanding = payment.outstanding || 0;
    if (Number(payment.amount) > outstanding) {
      return 'Amount marked for payment exceeds the outstanding amount available to link in the advance.';
    }
    return '';
  };

  // Per-row error, highest priority first: bad bank, then advance over-draw, then this row's Payment Summary bucket going negative.
  const rowError = (payment: InfoBlock): string => {
    return bankNotInLineItemsError(payment) || advanceOutstandingError(payment) || bankBalanceErrorForPayment(payment);
  };

  const buildBankGroups = (bookOfAccount: 'FCRA' | 'Non-FCRA') => {
    const items = (expenseDetails.grantsAndBudgetAllocation || []).filter(
      (item: any) => item.accountInfo?.bookOfAccount === bookOfAccount
    );
    const groups = new Map<string, { allocation: number; tds: number }>();
    items.forEach((item: any) => {
      const bank = item.accountInfo?.bankAccount || 'Bank not selected';
      const existing = groups.get(bank) || { allocation: 0, tds: 0 };
      existing.allocation += item.allocationAmount || 0;
      existing.tds += item.accountInfo?.tdsAmount || 0;
      groups.set(bank, existing);
    });

    const paymentTotalsForBook = bankPaymentTotals.value.get(bookOfAccount) || new Map();
    // A payment recorded against a bank (or "Bank not selected") with no matching line
    // item should still surface as its own row here.
    paymentTotalsForBook.forEach((_totals, label) => {
      if (!groups.has(label)) groups.set(label, { allocation: 0, tds: 0 });
    });

    return Array.from(groups.entries()).map(([label, sums]) => {
      const payable = sums.allocation - sums.tds;
      const paymentTotals = paymentTotalsForBook.get(label) || { payments: 0, viaAdvances: 0 };
      const paid = paymentTotals.payments + paymentTotals.viaAdvances;
      return {
        label,
        allocation: sums.allocation,
        tds: sums.tds,
        payable,
        payments: paymentTotals.payments,
        viaAdvances: paymentTotals.viaAdvances,
        paid,
        balance: payable - paid
      };
    });
  };

  const nonFcraBankGroups = computed(() => buildBankGroups('Non-FCRA'));
  const fcraBankGroups = computed(() => buildBankGroups('FCRA'));

  // Keyed by book-of-account + label, since "Bank not selected" is a shared fallback
  // that can occur under both FCRA and Non-FCRA independently — a flat label-only key
  // would let a Non-FCRA "Bank not selected" payment validate against the FCRA payable
  // (or vice versa).
  const bankPayableByLabel = computed(() => {
    const map = new Map<string, number>();
    nonFcraBankGroups.value.forEach(group => map.set(`Non-FCRA::${group.label}`, group.payable));
    fcraBankGroups.value.forEach(group => map.set(`FCRA::${group.label}`, group.payable));
    return map;
  });

  // Every payment against a given bank counts toward that bank's total — regular
  // payments and advance-linked payments alike — and that total must never exceed
  // the Payable (Net of TDS) amount the Payment Summary shows for that same bank.
  // A payment with no bank selected counts toward its book of account's own
  // "Bank not selected" bucket, same as the Payment Summary table does.
  // The Payment Summary bucket a payment rolls into: "<book>::<bank label>" (no-bank payments go to that book's "Bank not selected").
  const bankBucketKey = (payment: InfoBlock): string | null => {
    const bookOfAccount = payment.bookOfAccounts;
    if (bookOfAccount !== 'FCRA' && bookOfAccount !== 'Non-FCRA') return null;
    const label = (payment.bankUid && bankLabelByUid.value.get(payment.bankUid)) || 'Bank not selected';
    return `${bookOfAccount}::${label}`;
  };

  const bankBucketTotals = (): Map<string, number> => {
    const totals = new Map<string, number>();
    formState.value.forEach(payment => {
      const key = bankBucketKey(payment);
      if (!key) return;
      totals.set(key, (totals.get(key) || 0) + (payment.amount || 0));
    });
    return totals;
  };

  const bankBucketOverpaidMessage = (key: string, totalPaid: number): string => {
    const [bookOfAccount, label] = key.split('::');
    const payable = bankPayableByLabel.value.get(key) ?? 0;
    const target =
      label === 'Bank not selected' ? `No-bank ${bookOfAccount} line items` : `${label} (${bookOfAccount})`;
    return `${target}: ${formatToIndianCurrency(payable)} payable, overpaid by ${formatToIndianCurrency(totalPaid - payable)}.`;
  };

  const validateBankPayments = (): string | null => {
    for (const [key, totalPaid] of bankBucketTotals().entries()) {
      if (totalPaid > (bankPayableByLabel.value.get(key) ?? 0)) {
        return bankBucketOverpaidMessage(key, totalPaid);
      }
    }
    return null;
  };

  // Row-scoped version of the bank check: the overpaid message, only when this payment's own bucket would go negative.
  const bankBalanceErrorForPayment = (payment: InfoBlock): string => {
    const key = bankBucketKey(payment);
    if (!key) return '';
    const totalPaid = bankBucketTotals().get(key) ?? 0;
    return totalPaid > (bankPayableByLabel.value.get(key) ?? 0) ? bankBucketOverpaidMessage(key, totalPaid) : '';
  };

  // If any bucket would go negative, open the Payment Summary panel so the red Balance (A-B) is visible.
  const openSummaryIfBankOverpaid = () => {
    if (validateBankPayments() && !activeKey.value.includes('1')) {
      activeKey.value = ['1'];
    }
  };

  const addPayment = (isAdvance: boolean) => {
    formState.value.push({
      id: Date.now(),
      isAdvance: isAdvance,
      advance: '',
      advancePaymentId: '',
      paymentDate: '',
      paymentMode: '',
      referenceNumber: '',
      bookOfAccounts: '',
      amount: null,
      amountPaid: null,
      outstanding: null,
      originalAmount: null,
      error: '',
      bankUid: ''
    });
  };

  const removePayment = (id: number) => {
    formState.value = formState.value.filter(payment => payment.id !== id);
    openSummaryIfBankOverpaid();
  };

  const updateFormState = (value: string, id: number, key: string) => {
    formState.value = formState.value.map(payment => {
      if (payment.id === id) {
        (payment as any)[key] = value;
      }
      return payment;
    });
    if (key === 'bankUid') {
      const payment = formState.value.find(p => p.id === id);
      if (payment) setPaymentError(id, rowError(payment));
      openSummaryIfBankOverpaid();
    }
    if (key === 'amount') {
      if (amountCheckTimers.has(id)) clearTimeout(amountCheckTimers.get(id)!);
      const timer = setTimeout(() => {
        amountCheckTimers.delete(id);
        const payment = formState.value.find(p => p.id === id);
        if (payment) setPaymentError(id, rowError(payment));
        openSummaryIfBankOverpaid();
      }, 800);
      amountCheckTimers.set(id, timer);
    }
  };

  const updateBookOfAccounts = (val: string, paymentId: number) => {
    formState.value = formState.value.map(p => (p.id === paymentId ? { ...p, bookOfAccounts: val, bankUid: '' } : p));
    const payment = formState.value.find(p => p.id === paymentId);
    if (payment) setPaymentError(paymentId, rowError(payment));
    openSummaryIfBankOverpaid();
  };

  const updatePaymentMode = (val: string, paymentId: number) => {
    formState.value = formState.value.map(p =>
      p.id === paymentId ? { ...p, paymentMode: val, bankUid: val === 'Cash' ? '' : p.bankUid } : p
    );
  };

  const selectAdvancePaymentId = async (val: string, paymentId: number) => {
    const payment = formState.value.find(p => p.id === paymentId);
    if (!payment) return;
    const advancePayment = advanceSearchOptions.value
      .find(option => option.value === payment.advance)
      ?.advancePayments.find(item => item.value === val) as AdvancePaymentOptions;
    if (!advancePayment) return;

    // Step 1: set all fields including bookOfAccounts, clear bankUid so options update first
    formState.value = formState.value.map(p =>
      p.id === paymentId
        ? {
            ...p,
            advancePaymentId: val,
            paymentDate: advancePayment.paymentDate,
            paymentMode: advancePayment.paymentMode,
            bookOfAccounts: advancePayment.bookOfAccounts,
            referenceNumber: advancePayment.referenceNumber,
            amountPaid: (advancePayment as any).amountPaid,
            outstanding: (advancePayment as any).outstanding,
            bankUid: ''
          }
        : p
    );
    // Step 2: after bookOfAccounts change re-renders with the correctly filtered bank
    // options, auto-fill the bank the linked advance payment was itself made from
    // (the field stays disabled for advance-linked payments — this is display only)
    await nextTick();
    formState.value = formState.value.map(p =>
      p.id === paymentId ? { ...p, bankUid: advancePayment.bankUid || '' } : p
    );
    const updatedPayment = formState.value.find(p => p.id === paymentId);
    if (updatedPayment) setPaymentError(paymentId, rowError(updatedPayment));
  };

  const validateForm = (): Promise<boolean> => {
    let hasError = false;
    formState.value.forEach(payment => {
      const requiredFields = payment.isAdvance
        ? ['advancePaymentId', 'advance', 'paymentDate', 'paymentMode', 'bookOfAccounts', 'amount']
        : ['paymentDate', 'paymentMode', 'bookOfAccounts', 'amount'];

      const missingField = requiredFields.some(field => {
        if (field === 'amount') {
          return payment.amount === null || payment.amount === undefined;
        }
        return !payment[field as keyof InfoBlock];
      });

      let error = '';
      if (missingField) {
        error = 'All fields are required!';
      } else {
        error = rowError(payment);
      }
      if (error) {
        hasError = true;
        setPaymentError(payment.id, error);
      }
    });

    return hasError ? Promise.reject(false) : Promise.resolve(true);
  };

  onUnmounted(() => {
    amountCheckTimers.forEach(timer => clearTimeout(timer));
    amountCheckTimers.clear();
  });

  const updatePayment = async () => {
    try {
      const isFormValid = await validateForm();
      if (!isFormValid) {
        return;
      }

      const bankValidationError = validateBankPayments();
      if (bankValidationError) {
        activeKey.value = ['1'];
        paymentErrorMessage.value = bankValidationError;
        return;
      }
      paymentErrorMessage.value = '';

      updatePaymentStatus.value = 'pending';
      const formData = updatePaymentFormData(formState.value);
      const response = await post<PaymentUpdateResponse>(`/v1/save_payments_exp_list/${identifier}`, {
        data: formData
      });

      if (response?.data?.status === 'success') {
        openSuccessfulCustomNotification(`Payment updated successfully!`);
        updatePaymentStatus.value = 'success';
        onClose();
      } else if (response?.data?.status === 'error') {
        activeKey.value = ['1'];
        updatePaymentStatus.value = 'error';
        if (response?.data?.message && response.data.message.includes('exceeds outstanding')) {
          paymentErrorMessage.value = response.data.message;
        } else {
          paymentErrorMessage.value = '';
          openFailedCustomNotification(response?.data?.message);
        }
      } else {
        updatePaymentStatus.value = 'error';
        openFailedCustomNotification('Something went wrong while updating payment!');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      updatePaymentStatus.value = 'error';
      // validateForm rejected: inline row error is already set; also open the Payment Summary panel.
      openSummaryIfBankOverpaid();
    }
  };

  onMounted(async () => {
    getDropdownDataStatus.value = 'pending';

    try {
      const advanceDropdownResponse = await get<AdvancePaymentResponse>('/v1/get_advance_payments_line_item');

      const advanceDropdownData = advanceDropdownResponse?.data?.data?.map(advanceOption => {
        const advancePaymentOptions: {
          title: string;
          value: string;
          advancePayments: {
            title: string;
            value: string;
            amountPaid: number;
            outstanding: number;
            paymentDate: Dayjs;
            paymentMode: string;
            referenceNumber: string;
            bookOfAccounts: string;
            bankUid: string;
          }[];
        } = {
          title: advanceOption['advance-head'],
          value: advanceOption['advance-head'],
          advancePayments: []
        };

        advancePaymentOptions.advancePayments = Object.entries(advanceOption['advance-line-items']).map(
          ([key, payment]) => {
            const paymentIndex = parseInt(key);
            return {
              title: `${key}-${formatDateWithSlash(payment[`payment_date_${paymentIndex}`])}-${payment[`payment_mode_${paymentIndex}`]}-${payment[`payment_fcra_status_${paymentIndex}`]}   ${formatToIndianCurrency(Number(payment[`payment_amount_${paymentIndex}`]))}`,
              value: `${key}-${formatDateWithSlash(payment[`payment_date_${paymentIndex}`])}-${payment[`payment_mode_${paymentIndex}`]}-${payment[`payment_fcra_status_${paymentIndex}`]}`,
              amountPaid: +payment[`payment_amount_${paymentIndex}`],
              outstanding: +payment[`advance_outstanding_${paymentIndex}`],
              paymentDate: toDateTime(payment[`payment_date_${paymentIndex}`]),
              paymentMode: payment[`payment_mode_${paymentIndex}`],
              referenceNumber: payment[`payment_ref_${paymentIndex}`],
              bookOfAccounts: payment[`payment_fcra_status_${paymentIndex}`],
              bankUid: payment[`payment_bank_uid_${paymentIndex}`]
                ? String(payment[`payment_bank_uid_${paymentIndex}`])
                : ''
            };
          }
        );
        return advancePaymentOptions;
      });
      advanceSearchOptions.value = advanceDropdownData;
      orgBanksList.value = advanceDropdownResponse?.data?.org_banks_list || { FCRA: [], 'Non-FCRA': [] };
      getDropdownDataStatus.value = 'success';
    } catch (error) {
      getDropdownDataStatus.value = 'error';
      openFailedCustomNotification('Something went wrong while fetching input data!');
      console.error('Error fetching data:', error);
    }
    getDropdownDataStatus.value = 'pending';
    try {
      const response = await get<GetExpenseResponse>(`/v1/get_expense/${identifier}`);
      const selectedExpense = response?.data?.data?.selected_expense;
      expenseInfo.value = response?.data?.data?.selected_expense?.payment_stats;
      approvalStatus.value = response?.data?.data?.selected_expense?.approval_status;
      is_current_approver.value = response?.data?.data?.selected_expense?.is_current_approver;
      isFinalApprover.value = response?.data?.data?.selected_expense?.is_final_approver;
      cancelled.value = response?.data?.data?.selected_expense?.is_cancelled;
      const { expDetails, payments } = parseFormDataToState(selectedExpense);
      Object.assign(expenseDetails, cloneDeep(expDetails));
      const updatedPayments = payments.map((payment: any) => {
        if (payment.isAdvance) {
          const advancePayment = advanceSearchOptions.value.find(option => option.value === payment.advance);
          const advancePaymentOption = advancePayment?.advancePayments.find(
            item => item.value === payment.advancePaymentId
          );
          payment.amountPaid = advancePaymentOption?.amountPaid;
          payment.outstanding = advancePaymentOption?.outstanding;
          payment.originalAmount = payment.amount;
        }
        return payment;
      });
      // Step 1: set payments with bankUid cleared so options render first
      formState.value = updatedPayments.map((p: any) => ({ ...p, bankUid: '' }));
      // Step 2: after options are registered, set bankUid so label lookup succeeds
      await nextTick();
      formState.value = formState.value.map((p, i) => ({ ...p, bankUid: updatedPayments[i].bankUid }));
      getDropdownDataStatus.value = 'success';
    } catch (error: any) {
      getDropdownDataStatus.value = 'error';
      openFailedCustomNotification('Something went wrong while fetching expense data!');
      console.error('Error while fetching expense data: ', error);
    }
  });

  const isPaymentDisabled = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isCurrentApprover = is_current_approver.value;
    const isLastApprover = isFinalApprover.value;
    const isApproved = approvalStatus.value === 'Approved';
    const isNoApprover = approvalStatus.value === '0 of 0';
    const isExpenseControlAdmin = role.value.some(r => r.endsWith('_approval_control_admin'));

    // const isCancelled = cancelled?.value;

    if (isExpenseControlAdmin && !isNotSubmitted) {
      return false;
    }

    if (isNotSubmitted && isExpenseSubmitter) {
      return false;
    }

    if (isExpenseSubmitter && isNoApprover) {
      return false;
    }

    if (isExpenseModuleAdmin && isNotSubmitted) {
      return false;
    }

    if (isExpenseModuleAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isExpenseApprover && isNotSubmitted && isCurrentApprover) {
      return false;
    }

    if (isExpenseApprover && !isNotSubmitted && isCurrentApprover && (!isApproved || !isNoApprover)) {
      return false;
    }

    if (isExpenseApprover && isLastApprover && (isApproved || isNoApprover)) {
      return false;
    }

    if (
      (isAdmin || isExpenseApprover || isAdmin) &&
      (!isApproved || !isNoApprover) &&
      !isNotSubmitted &&
      isCurrentApprover
    ) {
      return false;
    }

    return true;
  });

  const isLinkAdvanceDisabled = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isCurrentApprover = is_current_approver.value;
    const isLastApprover = isFinalApprover.value;
    const isApproved = approvalStatus.value === 'Approved';
    const isNoApprover = approvalStatus.value === '0 of 0';
    const isExpenseControlAdmin = role.value.some(r => r.endsWith('_approval_control_admin'));
    // const isCancelled = cancelled?.value;

    if (isExpenseControlAdmin && !isNotSubmitted) {
      return false;
    }

    if (isNotSubmitted && isExpenseSubmitter) {
      return false;
    }

    if (isExpenseSubmitter && isNoApprover) {
      return false;
    }

    if (isExpenseModuleAdmin && isNotSubmitted) {
      return false;
    }

    if (isExpenseModuleAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isExpenseApprover && isNotSubmitted && isCurrentApprover) {
      return false;
    }

    if (isExpenseApprover && !isNotSubmitted && isCurrentApprover && (!isApproved || !isNoApprover)) {
      return false;
    }

    if (isExpenseApprover && isLastApprover && (isApproved || isNoApprover)) {
      return false;
    }

    if ((isAdmin || isExpenseApprover) && (!isApproved || !isNoApprover) && !isNotSubmitted && isCurrentApprover) {
      return false;
    }

    return true;
  });

  const isAddPaymentDisabled = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isLastApprover = isFinalApprover.value;
    const isApproved = approvalStatus.value === 'Approved';
    const isNoApprover = approvalStatus.value === '0 of 0';
    const isExpenseControlAdmin = role.value.some(r => r.endsWith('_approval_control_admin'));
    // const isCancelled = cancelled?.value;

    if (isExpenseControlAdmin && !isNotSubmitted) {
      return false;
    }

    if (isNotSubmitted && isExpenseSubmitter) {
      return true;
    }

    if (isExpenseSubmitter && isNoApprover) {
      return false;
    }

    if (isExpenseModuleAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isExpenseApprover && isLastApprover && (isApproved || isNoApprover)) {
      return false;
    }

    return true;
  });

  const isEditDisabled = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isAdvanceCurrentApprover = is_current_approver?.value;
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved';
    const isNoApprover = approvalStatus?.value === '0 of 0';
    const isAdvanceCancelled = cancelled?.value;

    if ((isExpenseSubmitter || isAdmin || isExpenseModuleAdmin) && isNoApprover) {
      return false;
    }

    if (isNotSubmitted && isExpenseSubmitter && isAdvanceCancelled) {
      return false;
    }

    if (isExpenseSubmitter && isNotSubmitted && !isAdvanceCancelled) {
      return false;
    }

    if (isExpenseModuleAdmin && isNotSubmitted) {
      return false;
    }

    if (isExpenseModuleAdmin && isApproved) {
      return false;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (!isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return true;
    }

    if (isAdmin && isApproved) {
      return false;
    }

    if (isExpenseModuleAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    if ((isExpenseApprover || isExpenseModuleAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isExpenseApprover && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isApproved && isLastApprover) {
      return false;
    }

    if (isExpenseApprover && isLastApprover && (isApproved || isNoApprover)) {
      return false;
    }

    return true;
  });

  const isPositiveAmount = (val: number | string | null | undefined) => {
    if (val === 'N.A.' || val === null || val === undefined || val === '') return false;
    const numeric = typeof val === 'number' ? val : parseFloat(String(val).replace(/[₹,\s]/g, ''));
    return !isNaN(numeric) && numeric > 0;
  };

  const showBalancePayments = computed(() => isPositiveAmount(expenseDetails.balancePayments));

  const showBalanceForFCRA = computed(
    () => showBalancePayments.value && isPositiveAmount(expenseDetails.balanceForFCRA)
  );

  const showBalanceForNonFCRA = computed(
    () => showBalancePayments.value && isPositiveAmount(expenseDetails.balanceForNonFCRA)
  );

  const showRemainingToBeUnallocated = computed(() => isPositiveAmount(expenseDetails.remaining_to_be_unallocated));
</script>

<template>
  <a-drawer
    v-if="identifier"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    title="Payment Information"
    :open="identifier !== null"
    width="1220"
    @close="onClose"
  >
    <template #extra>
      <div class="flex gap-2 items-center">
        <RoleBasedButton
          component-name="updatePaymentActionButton"
          :disabled="isEditDisabled"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="() => navigateTo({ path: '/expenses/edit-expense', query: { identifier } })"
        >
          <PencilIcon class="h-4 w-4" />
          Edit Expense
        </RoleBasedButton>
        <!-- <a-button
          class="flex rounded items-center gap-1 sm-medium py-1.5 px-2 justify-center shadow-[0_1px_2px_0px_#0000000D]"
          @click="onClose"
        >
          Cancel
        </a-button> -->
        <RoleBasedButton
          component-name="updatePaymentActionButton"
          :disabled="isPaymentDisabled"
          active-class="!text-white hover:!text-white bg-blue-700 hover:bg-blue-600 disabled:!text-blue-300 disabled:border-blue-300 disabled:bg-blue-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-blue-700 hover:!border-blue-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-gray-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          :loading="updatePaymentStatus === 'pending'"
          @click="updatePayment"
        >
          Update Payment
        </RoleBasedButton>
      </div>
    </template>
    <div v-if="paymentErrorMessage" class="border border-b py-4 px-3">
      <a-alert :message="paymentErrorMessage" type="error" show-icon />
    </div>
    <PaymentLoadingState v-if="getDropdownDataStatus === 'pending'" />
    <DrawerLayout
      v-else
      :response-state="responseState"
      main-display="null"
      sub-display="null"
      side-display="null"
      no-header-display
      @close="onClose"
    >
      <template #column1>
        <div v-if="formState.length === 0" class="flex min-h-[243px] bg-gray-50 w-full justify-center items-center">
          <p class="sm-medium text-gray-500">No Payments Linked.</p>
        </div>
        <a-form
          v-else
          class="flex flex-col w-full p-3 gap-3 min-h-[243px] paymentManagement"
          :colon="false"
          :label-col="{ style: { width: '158px' } }"
          label-align="left"
          :hide-required-mark="true"
          :disabled="updatePaymentStatus === 'pending'"
          @submit="updatePayment"
        >
          <div
            v-for="payment in formState"
            :key="payment.id"
            class="flex border border-gray-200 rounded-lg shadow-[0_1px_4px_0px_#0C0C0D0D]"
          >
            <div class="flex flex-col gap-3 p-3 w-[420px] border-r border-gray-200">
              <CustomDropdownWithSearch
                v-if="payment.isAdvance"
                label-text="Advance"
                :value="payment.advance"
                name="advance"
                :is-compulsory="true"
                state-key="advance"
                :options="advanceSearchOptions"
                :disabled="updatePaymentStatus === 'pending'"
                :width="'200px !important'"
                :dropdown-match-select-width="false"
                dropdown-min-width="400px"
                label-width="158px"
                @update:value="
                  val => {
                    // reset other details when Advance is selected
                    formState = formState.map(paymentItem => {
                      if (paymentItem.id === payment.id) {
                        paymentItem.advance = val;
                        paymentItem.advancePaymentId = '';
                        paymentItem.paymentDate = '';
                        paymentItem.paymentMode = '';
                        paymentItem.bookOfAccounts = '';
                        paymentItem.referenceNumber = '';
                        paymentItem.amount = null;
                        paymentItem.amountPaid = null;
                        paymentItem.outstanding = null;
                        paymentItem.bankUid = '';
                      }
                      return paymentItem;
                    });
                  }
                "
              />
              <CustomDropDownAdvanceUIDSearch
                v-if="payment.isAdvance"
                label-text="Advance Payment ID"
                :value="payment.advancePaymentId"
                name="advancePaymentId"
                :is-compulsory="true"
                :is-disabled="!payment.advance || updatePaymentStatus === 'pending'"
                :options="
                  advanceSearchOptions
                    .find(option => option.value === payment.advance)
                    ?.advancePayments.map(payment => ({
                      title: payment.title,
                      value: payment.value
                    })) || []
                "
                @update:value="val => selectAdvancePaymentId(val, payment.id)"
              />
              <CustomDate
                label-text="Payment Date"
                :value="payment.paymentDate"
                name="paymentDate"
                :is-compulsory="true"
                state-key="paymentDate"
                :disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updateFormState(val, payment.id, 'paymentDate')"
              />
              <CustomDropdown
                label-text="Payment Mode"
                :value="payment.paymentMode"
                name="paymentMode"
                :is-compulsory="true"
                :options="paymentModes"
                :state-key="`paymentMode`"
                label-width="158px"
                :disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updatePaymentMode(val, payment.id)"
              />
              <CustomInput
                label-text="Reference Number"
                :value="payment.referenceNumber"
                name="referenceNumber"
                :state-key="`referenceNumber`"
                :is-disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updateFormState(val, payment.id, 'referenceNumber')"
              />
              <CustomDropdown
                label-text="Book of Accounts"
                :value="payment.bookOfAccounts"
                name="bookOfAccounts"
                :state-key="`bookOfAccounts`"
                :is-compulsory="true"
                :options="bookOfAccounts"
                label-width="158px"
                :disabled="payment.isAdvance || updatePaymentStatus === 'pending'"
                @update:value="val => updateBookOfAccounts(val, payment.id)"
              />
              <CustomDropdownWithSearch
                label-text="Bank Details"
                :value="payment.bankUid || ''"
                :name="`bankUid_${payment.id}`"
                :state-key="`bankUid_${payment.id}`"
                :options="bankOptionsFor(payment.bookOfAccounts)"
                :allow-clear="true"
                label-width="158px"
                :is-disabled="
                  payment.isAdvance ||
                  !payment.bookOfAccounts ||
                  payment.paymentMode === 'Cash' ||
                  updatePaymentStatus === 'pending'
                "
                @update:value="val => updateFormState(val, payment.id, 'bankUid')"
              />
            </div>
            <div class="flex flex-col gap-2 bg-gray-50 p-3 rounded-r-lg justify-between">
              <div class="flex flex-col gap-3 min-w-0">
                <CustomRupeeInput
                  label-text="Amount"
                  :value="payment.amount"
                  name="amount"
                  :is-compulsory="true"
                  :disabled="updatePaymentStatus === 'pending'"
                  :state-key="`amount`"
                  @update:value="val => updateFormState(val, payment.id, 'amount')"
                />
                <div
                  v-if="payment.isAdvance && payment.advancePaymentId"
                  class="flex flex-col gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200"
                >
                  <p class="text-gray-400 sm-medium">Payment Info</p>
                  <div class="flex items-center justify-between text-gray-600">
                    <p>Amount Paid</p>
                    <p>{{ formatToIndianCurrency(payment.amountPaid) }}</p>
                  </div>
                  <div class="flex items-center justify-between text-gray-600">
                    <p>Outstanding</p>
                    <p>{{ formatToIndianCurrency(payment.outstanding) }}</p>
                  </div>
                </div>
                <Transition name="payment-error">
                  <a-alert
                    v-if="payment.error"
                    :message="payment.error"
                    type="error"
                    show-icon
                    class="payment-error-alert !rounded-md w-0 min-w-full"
                  >
                    <template #action>
                      <button
                        type="button"
                        class="text-gray-400 transition-colors hover:text-gray-600"
                        aria-label="Dismiss"
                        @click="setPaymentError(payment.id, '')"
                      >
                        <XIcon class="h-3.5 w-3.5" />
                      </button>
                    </template>
                  </a-alert>
                </Transition>
              </div>
              <div class="flex">
                <a-button
                  class="self-end !bg-gray-50 !border-none !shadow-none !text-red-600 !w-fit !h-fit !p-0 sm-medium"
                  :disabled="updatePaymentStatus === 'pending'"
                  @click="removePayment(payment.id)"
                >
                  Remove
                </a-button>
              </div>
            </div>
          </div>
        </a-form>
        <div class="flex gap-3 px-3 py-5 border-b border-gray-100 mb-10">
          <a-button
            class="flex items-center gap-2 !p-0 !w-fit !h-fit !border-none !bg-transparent !shadow-none text-blue-700"
            :disabled="isAddPaymentDisabled"
            @click="addPayment(false)"
          >
            <PlusIcon class="h-4 w-4" />
            Add Payment
          </a-button>
          <a-button
            class="flex items-center gap-2 !p-0 !w-fit !h-fit !border-none !bg-transparent !shadow-none text-blue-700"
            :disabled="isLinkAdvanceDisabled"
            @click="addPayment(true)"
          >
            <PlusIcon class="h-4 w-4" />
            Link Advance
          </a-button>
        </div>
      </template>
      <template #column2>
        <div class="border border-zinc-200 bg-white rounded shadow-[0px_1px_3px_0px_#0000001A]">
          <h2 class="border-b-2 border-zinc-200 px-3 py-2 text-zinc-600 font-semibold">Expense Payment Info</h2>
          <div class="flex flex-col">
            <div class="px-3 py-2 flex flex-col gap-2">
              <div class="flex justify-between items-center">
                <div class="flex gap-1 items-center">
                  <p class="sm-medium text-zinc-500">Total amount</p>
                  <InformationCircle title="Total Donations booked across the system" />
                </div>
                <p class="sm-medium">
                  {{ formatToIndianCurrency(expenseInfo.total_amount) }}
                </p>
              </div>
              <div class="flex justify-between items-center">
                <div class="flex gap-1 items-center">
                  <p class="sm-medium text-zinc-500">TDS</p>
                  <InformationCircle title="Pledges / Grants tranches committed, for a past date but not completed" />
                </div>
                <p class="sm-medium">
                  {{ formatToIndianCurrency(expenseInfo.tds) }}
                </p>
              </div>
            </div>

            <div class="px-3 py-2 flex flex-col gap-2 border-t">
              <div class="flex justify-between items-center">
                <div class="flex gap-1 items-center">
                  <p class="sm-medium text-zinc-500">Payable</p>
                  <InformationCircle title="Pledges / Grants tranches upcoming, for a future date." />
                </div>
                <p class="sm-medium text-zinc-800">
                  {{ formatToIndianCurrency(expenseInfo.payable) }}
                </p>
              </div>
              <div class="flex justify-between items-center">
                <p class="sm-medium text-zinc-500">Amount Paid</p>
                <p class="sm-medium text-zinc-800">
                  {{
                    formatToIndianCurrency(isNaN(Number(expenseInfo.amount_paid)) ? 0 : Number(expenseInfo.amount_paid))
                  }}
                </p>
              </div>
            </div>
          </div>
          <div class="flex justify-between items-center border-t-2 border-zinc-200 px-3 py-2">
            <p class="sm-medium text-zinc-500">Balance</p>
            <p class="sm-medium text-[#DC2626]">
              {{ formatToIndianCurrency(expenseInfo.balance) }}
            </p>
          </div>
        </div>
        <!-- <div
          :class="`bg-white border border-gray-200 rounded shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] `"
        >
          <div class="flex justify-between w-full p-3">
            <span class="font-medium text-sm text-gray-600">Total Amount Paid</span>
            <span class="font-bold text-sm text-gray-700"> {{ expenseDetails.totalAmountPaid }}</span>
          </div>
        </div> -->
        <div
          class="flex flex-col bg-white text-gray-600 sm-medium w-full rounded shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] overflow-hidden"
        >
          <!-- <div v-if="showBalancePayments" class="flex items-center justify-between w-full p-3 border-b border-gray-200">
            <p>Balance Payments</p>
            <p class="text-red-700">{{ expenseDetails.balancePayments }}</p>
          </div> -->
          <div
            v-if="showBalanceForFCRA"
            class="flex items-center justify-between w-full px-3 py-2 border-b border-gray-200"
          >
            <div class="flex gap-2 items-center sm-medium">
              <!-- <ExclamationIcon class="h-4 w-4 text-red-600" /> -->
              <p>Balance from FCRA</p>
            </div>
            <p>{{ expenseDetails.balanceForFCRA }}</p>
          </div>
          <div
            v-if="showBalanceForNonFCRA"
            class="flex items-center justify-between w-full px-3 py-2 border-b border-gray-200"
          >
            <p>Balance from Non-FCRA</p>
            <p>{{ expenseDetails.balanceForNonFCRA }}</p>
          </div>
          <div
            v-if="showRemainingToBeUnallocated"
            class="flex items-center justify-between w-full px-3 py-2 border-b border-gray-200"
          >
            <p>Balance from Unallocated</p>
            <p>{{ expenseDetails.remaining_to_be_unallocated }}</p>
          </div>
        </div>
        <ExpenseTotalAmountModal
          :total-invoice-amount="expenseDetails.totalInvoiceAmount"
          :allocation-details="expenseDetails.grantsAndBudgetAllocation"
        />
        <AuditLogs :items="expenseDetails.auditLog" title="Expense" />
      </template>
    </DrawerLayout>
    <a-collapse
      v-model:active-key="activeKey"
      class="fixed bottom-0 right-0 w-[1220px] paymentCollapse"
      :default-active-key="['1']"
    >
      <template #expandIcon="{ isActive }">
        <ChevronDownIcon class="text-black w-4 h-4" :class="{ 'rotate-180': !isActive }" />
      </template>
      <a-collapse-panel key="1" header="Payment Summary" class="">
        <div class="rounded shadow-[0_1px_4px_0px_#0C0C0D0D] overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full paymentSummaryTable">
              <thead>
                <tr class="bg-gray-200">
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[180px] px-3 py-2.5"
                  >
                    Fund / Bank
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[150px] px-3 py-2.5 text-right"
                  >
                    Expense Allocation
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[90px] px-3 py-2.5 text-right"
                  >
                    TDS
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[190px] px-3 py-2.5 text-right"
                  >
                    Payable (Net of TDS) - (A)
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[100px] px-3 py-2.5 text-right"
                  >
                    Payments
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[165px] px-3 py-2.5 text-right"
                  >
                    Payments via Advances
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[175px] px-3 py-2.5 text-right"
                  >
                    Total Amount Paid - (B)
                  </td>
                  <td
                    class="border border-gray-300 text-xs font-semibold text-gray-700 whitespace-nowrap min-w-[115px] px-3 py-2.5 text-right"
                  >
                    Balance (A-B)
                  </td>
                </tr>
              </thead>
              <tbody>
                <!-- NON-FCRA -->
                <PaymentSummaryFundRow
                  label="Non-FCRA"
                  :collapsible="nonFcraBankGroups.length > 0"
                  :expanded="nonFcraExpanded"
                  :allocation="expenseDetails.totalExpenseAllocationNonFcra"
                  :tds="expenseDetails.tdsNonFcra"
                  :payable="expenseDetails.payableNetOfTdsNonFcra"
                  :payments="paymentsNonFcra"
                  :via-advances="paymentsViaAdvancesNonFcra"
                  :paid="totalPaymentsAndAdvancesNonFcra"
                  :balance="balanceNonFcra"
                  @toggle="nonFcraExpanded = !nonFcraExpanded"
                />
                <template v-if="nonFcraExpanded">
                  <PaymentSummaryBankRow
                    v-for="group in nonFcraBankGroups"
                    :key="`non-fcra-${group.label}`"
                    :label="group.label"
                    :allocation="group.allocation"
                    :tds="group.tds"
                    :payable="group.payable"
                    :payments="group.payments"
                    :via-advances="group.viaAdvances"
                    :paid="group.paid"
                    :balance="group.balance"
                  />
                </template>
                <!-- FCRA -->
                <PaymentSummaryFundRow
                  label="FCRA"
                  :collapsible="fcraBankGroups.length > 0"
                  :expanded="fcraExpanded"
                  :allocation="expenseDetails.totalExpenseAllocationFcra"
                  :tds="expenseDetails.tdsFcra"
                  :payable="expenseDetails.payableNetOfTdsFcra"
                  :payments="paymentsFcra"
                  :via-advances="paymentsViaAdvancesFcra"
                  :paid="totalPaymentsAndAdvancesFcra"
                  :balance="balanceFcra"
                  @toggle="fcraExpanded = !fcraExpanded"
                />
                <template v-if="fcraExpanded">
                  <PaymentSummaryBankRow
                    v-for="group in fcraBankGroups"
                    :key="`fcra-${group.label}`"
                    :label="group.label"
                    :allocation="group.allocation"
                    :tds="group.tds"
                    :payable="group.payable"
                    :payments="group.payments"
                    :via-advances="group.viaAdvances"
                    :paid="group.paid"
                    :balance="group.balance"
                  />
                </template>
                <!-- UNALLOCATED (grant not selected) — no bank breakdown -->
                <PaymentSummaryFundRow
                  label="Unallocated"
                  :allocation="expenseDetails.totalUnallocatedTotalAmount"
                  :tds="expenseDetails.unallocatedTdsAmount"
                  :payable="expenseDetails.payableNetOfTdsUnallocated"
                  :payments="paymentsUnallocated"
                  :via-advances="paymentsViaAdvancesUnallocated"
                  :paid="totalPaymentsAndAdvancesUnallocated"
                  :balance="balanceUnallocated"
                />
                <!-- TOTAL -->
                <PaymentSummaryFundRow
                  label="Total"
                  bold
                  :allocation="expenseDetails.totalExpenseAllocation"
                  :tds="expenseDetails.totalTdsAmount"
                  :payable="expenseDetails.payableNetOfTds"
                  :payments="totalPayments"
                  :via-advances="totalPaymentsViaAdvances"
                  :paid="totalPaymentAndAdvancesBoth"
                  :balance="balanceBoth"
                />
              </tbody>
            </table>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .paymentManagement .ant-picker {
    width: 206px;
  }

  .paymentManagement .ant-input {
    width: 206px;
  }

  .paymentManagement .ant-select-selector {
    width: 206px !important;
  }

  .paymentManagement .ant-select {
    width: 206px !important;
  }

  .paymentManagement .ant-form-item-control-input-content {
    width: 206px !important;
  }

  .paymentManagement .ant-input-number {
    width: 145px !important;
  }

  .paymentManagement .ant-form-item {
    margin-bottom: 0px;
  }

  .paymentCollapse .ant-collapse-expand-icon {
    margin-top: 1px;
    margin-right: 8px;
    border: 1px solid #d4d4d8;
    padding: 0px !important;
    background: #fff;
    height: 20px !important;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    z-index: 1;
  }

  .paymentCollapse .ant-collapse-item {
    border-radius: 0px !important;
    border: 0px !important;
  }

  .paymentCollapse .ant-collapse {
    border-radius: 0px !important;
    border: 0px !important;
  }

  .paymentCollapse .ant-collapse-header {
    border-radius: 0px !important;
    border: 0px !important;
  }

  .paymentCollapse .ant-collapse-content {
    border-top: 0px !important;
  }

  .paymentCollapse .ant-collapse-content-box {
    padding: 12px !important;
    padding-top: 0px !important;
  }

  .paymentSummaryTable td {
    vertical-align: middle;
  }

  /* Vertically centre the status icon and the dismiss button against the wrapped message. */
  .payment-error-alert {
    align-items: center !important;
  }
  .payment-error-alert .ant-alert-icon,
  .payment-error-alert .ant-alert-action {
    align-self: center;
  }
  .payment-error-alert .ant-alert-action {
    display: flex;
    align-items: center;
    margin-inline-start: 8px;
  }

  /* One combined transition so the box and its text fade + collapse together. */
  .payment-error-enter-active,
  .payment-error-leave-active {
    overflow: hidden;
    transition:
      opacity 0.3s ease,
      max-height 0.3s ease;
  }
  .payment-error-enter-from,
  .payment-error-leave-to {
    opacity: 0;
    max-height: 0;
  }
  .payment-error-enter-to,
  .payment-error-leave-from {
    opacity: 1;
    max-height: 200px;
  }
</style>
